import React, { useEffect, useRef, useState } from 'react';
import { DESTINATION_TYPES } from '../../../constants/destinationTypes';
import { AngularDiProviders } from '../../../../../injector/components/angular-di/AngularDiProviders';
import { AskSupportDialog } from '../../../../../support/components/ask-support-dialog/AskSupportDialog';
import { WizardStepBaseProps } from '../../../../../wizard/interfaces/wizardStep';
import useHasPermission from '../../../../../access-control/hooks/useHasPermission';
import { useObservableState } from '../../../../../core/hooks/useObservableState';
import styles from './DestinationSettings.module.scss';
import { getDestinationModeKeywords } from '../../../../../connector/connector-form/utils/utils';
import { HdDocLink, HdLink, HdPane } from '../../../../../components';
import { getDataIdGenerator } from '../../../../../core/utils/generateDataId';
import { HevoEntity } from '../../../../../core/interfaces/hevo-entity';
import { DestinationMode } from '../../../../../core/interfaces/destination-mode';
import useAnalyticsTracker from '../../../../../analytics/hooks/useAnalyticsTracker';
import {
  TRACKER_DEST_CONFIG_CONNECTION_SUCCESS,
  TRACKER_DEST_CONFIG_ON_FORM_FIELD_FOCUSIN,
  TRACKER_DEST_CONFIG_ON_FORM_FIELD_INTERACTION,
  TRACKER_SELECT_AUTH_TYPE_INVITE_MEMBER_LINK_CLICK
} from '../../../../../connector/connector-form/tracking/connectorFormTracking';
import { ConnectorFormShimmer } from '../../../../../connector/connector-form/components/connector-form-shimmer/ConnectorFormShimmer';
import HdMenuTrigger from '../../../../../menu/components/menu-trigger/HdMenuTrigger';
import useService from '../../../../../injector/hooks/useService';
import { DestinationSettingsService } from '../destination-settings-form/destination-settings.service';
import { getNextEntityNameForDefaultName } from '../../../../../core/utils/entity';
import { HdDropdownWithExternalTrigger } from '../../../../../dropdown/components/dropdown-with-external-trigger/DropdownWithExternalTrigger';
import DestinationSettingsForm from '../destination-settings-form/DestinationSettingsForm';
import { getErrorMessageFromObj } from '../../../../../core/utils/request';
import { AskSupportDialogService } from '../../../../../support/services/ask-support-dialog.service';
import { RefreshService } from '../../../../../core/services/refresh.service';
import {
  DestinationConfigData,
  DestinationFormData
} from '../destination-settings-form/destination-form-data';
import { getDestinationConfigFlatModel } from '../destination-settings-form/utils';
import {
  DEST_SETTINGS_SUPPORT_CONCERN,
  NODE_SETTINGS_ASK_SUPPORT_ERRORS_THRESHOLD
} from '../../../../../connector/connector-form/constants/constants';
import { NodeStrings } from '../../../../../connector/connector-form/constants/nodeStrings';
import { AddMemberDialog } from '../../../../team/AddMemberDialog';
import { SetupGuideDocs } from '../../../../../docs/components/setup-guide-docs/SetupGuideDocs';
import useTeamSettings from '../../../../team/hooks/useTeamSettingsService';

declare global {
  interface Window {
    ASK_SUPPORT_CONFIG: any;
  }
}

const angularDIProviders = [
  DestinationSettingsService
];

export function DestinationSettings({
  initialData,
  controller,
  completeStep,
  onDataChange,
  markDirty
}: WizardStepBaseProps) {
  /**
   * This is needed to avoid competing state updates due to subscription of formik changes
   */
  const dataToTrack = useRef(initialData);

  return (
    <AngularDiProviders providers={angularDIProviders}>
      <DestinationSettingsInner
        initialData={initialData}
        onDataChange={newData => {
          dataToTrack.current = {
            ...dataToTrack.current,
            ...newData
          };

          onDataChange(dataToTrack.current);
        }}
        stepController={controller}
        markDirty={markDirty}
        nextClick={completeStep}
      />
    </AngularDiProviders>
  );
}

export function DestinationSettingsInner({
  stepController,
  initialData,
  onDataChange,
  nextClick,
  markDirty
}) {
  const [loading, setLoading] = useState(false);
  const [selectedExistingDestination, setSelectedExistingDestination] = useState(null);
  const [openExistingDestinationMenu, setOpenExistingDestinationMenu] = useState(false);
  const [formLoadData, setFormLoadData] = useState(null);
  const [staticDependenciesLoaded, setStaticDependenciesLoaded] = useState(false);
  const [destinationList, setDestinationList] = useState([]);
  const [sampleSettings, setSampleSettings] = useState(null);
  const [frozenFields, setFrozenFields] = useState({});
  const [addMemberDialogVisible, setAddMemberDialogVisible] = useState(false);
  const [destination, setDestination] = useState<DestinationConfigData>(null);

  const isFormPristineOnInit = useRef(false);
  const isStepComplete = useRef(false);

  const { eventTrack } = useAnalyticsTracker();
  const { isRbacEnabled } = useHasPermission();

  const _destinationSettingsService = useService(DestinationSettingsService);
  const _askSupportDialogService = useService(AskSupportDialogService);
  const { getTeamSettings } = useTeamSettings();
  const _refreshService = useService(RefreshService);

  const dataIdGenerator = getDataIdGenerator('destination-settings');

  const { destinationType, draftDestination } = stepController.data;
  const { wizard } = stepController.params;
  const destinationMode = stepController.data.destinationMode || DestinationMode.DESTINATION;
  const hevoEntityType = stepController.params.hevoEntityType || HevoEntity.DESTINATION;
  const destinationModeKeywords = getDestinationModeKeywords(destinationMode);
  const destinationTypeName = destinationType.dest_type_display;
  const destinationTypeIdentifier = destinationType.dest_type;
  const destinationTypeMetaData = DESTINATION_TYPES[destinationTypeIdentifier];
  const _docLink =
    destinationMode === DestinationMode.WAREHOUSE && destinationTypeMetaData.warehouseDocLink
      ? destinationTypeMetaData.warehouseDocLink
      : destinationTypeMetaData.docLink;
  const [docLink, setDocLink] = useState(_docLink);

  const { canShowSideBySideDocs } = destinationTypeMetaData;
  const draftPipelineSettingsEnabled = !!getTeamSettings()?.draft_pipeline_enabled;
  const addMemberDialogData = {
    entity: HevoEntity.DESTINATION.value,
    wizard: stepController.params.wizard,
    loginNextURL: stepController.params.loginNextURL,
    type: destinationTypeName
  };

  const existingDestinationsPlaceholder = `Existing ${destinationModeKeywords.plural}`;

  useEffect(() => {
    setInitialFormLoadData();

    const askSupportDialogSub = _askSupportDialogService
      .askSupportOnFailure(
        DEST_SETTINGS_SUPPORT_CONCERN,
        error =>
          NodeStrings.getConnectionErrorSupportMessage(destinationTypeName, 'destination', error),
        window.ASK_SUPPORT_CONFIG?.error_count || NODE_SETTINGS_ASK_SUPPORT_ERRORS_THRESHOLD
      )
      .pipe()
      .subscribe();

    return () => {
      askSupportDialogSub.unsubscribe();
    };
  }, []);

  const setInitialFormLoadData = () => {
    let _formLoadData;

    if (!initialData.destination) {
      if (initialData.initialDestinationConfig) {
        _formLoadData = {
          destination: {
            isInitialConfig: true,
            ...initialData.initialDestinationConfig
          }
        };
      } else {
        _formLoadData = {
          destination: {}
        };
      }

      isFormPristineOnInit.current = true;
    } else {
      _formLoadData = {
        destination: initialData.destination
      };
    }

    if (initialData.freezeAutoPopulatedFields) {
      setFrozenFields(
        _destinationSettingsService.getInitialFrozenFields(destinationTypeIdentifier)
      );
    }

    setFormLoadData(_formLoadData);
  };

  const inviteMember = () => {
    setAddMemberDialogVisible(true);

    eventTrack({
      action: TRACKER_SELECT_AUTH_TYPE_INVITE_MEMBER_LINK_CLICK,
      properties: {
        ...addMemberDialogData
      }
    });
  };

  const closeAddMemberDialog = () => {
    setAddMemberDialogVisible(false);
  };

  const loadDestination = async () => {
    setLoading(true);

    let _destinationList = [];

    if (!staticDependenciesLoaded) {
      const [sample, existingList] = await _destinationSettingsService
        .getSampleSettings$(destinationTypeMetaData)
        .toPromise();

      _destinationList = existingList;

      setSampleSettings(sample);
      setDestinationList(existingList);
    }

    const res = await _destinationSettingsService
      .getNodeFromExistingData$(
        destinationTypeMetaData,
        formLoadData.destination,
        false,
        draftDestination
      )
      .toPromise();

    if (!staticDependenciesLoaded) {
      setStaticDependenciesLoaded(true);

      if (
        isFormPristineOnInit.current &&
        draftDestination?.dest_type !== destinationTypeMetaData.name
      ) {
        res.formData.name = getDefaultDestinationName(_destinationList);
      }
    }

    setDestination({ ...res });

    setLoading(false);
  };

  const getDefaultDestinationName = _destinationList => {
    const prefix = `${destinationTypeName} ${destinationModeKeywords.singular}`;

    return getNextEntityNameForDefaultName(
      prefix,
      _destinationList,
      _destination => _destination.name
    );
  };

  const onFormDirty = () => {
    if (isStepComplete.current === true) {
      return;
    }

    markDirty();
  };

  const onSubmit = (_destination: any) => {
    onDataChange({
      ...initialData,
      destination: _destination
    });

    isStepComplete.current = true;

    nextClick();
  };

  const onConnectionError = formError => {
    const errorMessage = getErrorMessageFromObj(formError);
    _askSupportDialogService.onStepError(errorMessage);

    onDataChange({
      ...initialData,
      testConnectionState: 'ERRORED',
      testConnectionError: errorMessage
    });
  };

  const onConnectionSuccess = () => {
    onDataChange({
      ...initialData,
      testConnectionState: 'SUCCESS',
      testConnectionError: null
    });

    eventTrack({
      action: TRACKER_DEST_CONFIG_CONNECTION_SUCCESS,
      properties: {
        destinationType: destinationTypeIdentifier,
        context: hevoEntityType.toString()
      }
    });
  };

  const onFormFieldChange = fieldPath => {
    eventTrack({
      action: TRACKER_DEST_CONFIG_ON_FORM_FIELD_INTERACTION,
      properties: {
        field: fieldPath,
        wizard,
        type: destinationType.dest_type_display
      }
    });
  };

  const onFormValueChanges = event => {
    if (draftPipelineSettingsEnabled) {
      _refreshService.saveDraftDestinationFormData(event.destination);
    }
  };

  const onFormFieldFocusIn = fieldPath => {
    eventTrack({
      action: TRACKER_DEST_CONFIG_ON_FORM_FIELD_FOCUSIN,
      properties: {
        field: fieldPath,
        wizard,
        type: destinationType.dest_type_display
      }
    });
  };

  const onFormDataChanges = (formData: DestinationFormData) => {
    destination.formData = formData;

    onDataChange({
      ...initialData,
      destination: getDestinationConfigFlatModel(destination)
    });

    onFormValueChanges({ destination: formData, formValid: null });
  };

  const sampleSettingsClick = () => {
    const currentDestinationName = destination.formData.name;

    setFormLoadData({
      destination: {
        name: currentDestinationName,
        isSample: true,
        ...sampleSettings
      }
    });
  };

  const applyExistingDestinationSettings = _destination => {
    const currentDestinationName = destination.formData.name;

    setSelectedExistingDestination(_destination);
    setOpenExistingDestinationMenu(false);

    setFormLoadData({
      destination: {
        name: currentDestinationName,
        isSample: true,
        ..._destination.config
      }
    });
  };

  useEffect(() => {
    if (formLoadData) {
      loadDestination();
    }
  }, [formLoadData]);

  const [askSupportDialogData] = useObservableState(_askSupportDialogService.askSupportDialogData$, { open: false });

  return (
    <div className={styles.destinationSettingsContainer}>
      <div className='scrollable-content bg-surface flex-1'>
        <div className='body-container'>
          <div className='header-container'>
            <div className='header'>
              Configure your {destinationTypeName} {destinationModeKeywords.singular}
            </div>

            {!loading && docLink ? (
              <div className='subHeader'>
                {!canShowSideBySideDocs ? (
                  <span>
                    <HdDocLink
                      dataId={dataIdGenerator('')}
                      docLink={docLink}
                      section='settings_header'
                    />{' '}
                    about how to configure a {destinationTypeName}{' '}
                    {destinationModeKeywords.singular}
                  </span>
                ) : (
                  <span>
                    Follow the guide on the right to set up your {destinationModeKeywords.singular}
                  </span>
                )}{' '}
                {!isRbacEnabled ? (
                  <>
                    or{' '}
                    <HdLink
                      tag='button'
                      onClick={inviteMember}
                      dataId={dataIdGenerator('invite-member')}
                    >
                      Invite a Team Member
                    </HdLink>{' '}
                    to do it for you
                  </>
                ) : null}
              </div>
            ) : null}
          </div>

          {loading && !destination && <ConnectorFormShimmer />}

          {destinationType && !loading && destination ? (
            <div
              className='settings-content'
              style={{ maxWidth: `${canShowSideBySideDocs ? '85%' : '820px'}` }}
            >
              <div className='cp-settings-form-header'>
                {destinationList.length > 0 && (
                  <span className={`use-sample ${styles['flexible-use-sample']}`}>
                    Please provide {destinationTypeName} connection settings or copy from one of the
                    <div>
                      <HdMenuTrigger
                        id='existing-destination'
                        onClick={() => {
                          setOpenExistingDestinationMenu(true);
                        }}
                        onForceClose={() => {
                          setOpenExistingDestinationMenu(false);
                        }}
                        dataId={dataIdGenerator('existing-destinations')}
                      >
                        {selectedExistingDestination?.name || existingDestinationsPlaceholder}
                      </HdMenuTrigger>

                      <HdDropdownWithExternalTrigger
                        placeholder={existingDestinationsPlaceholder}
                        dataId={dataIdGenerator('destination')}
                        id='existing-destination-menu'
                        target='#existing-destination'
                        selected={selectedExistingDestination}
                        open={openExistingDestinationMenu}
                        valueAccessor='name'
                        options={destinationList}
                        onChangeEventHandler={event => {
                          applyExistingDestinationSettings(event);
                        }}
                        onClose={() => {
                          setOpenExistingDestinationMenu(false);
                        }}
                      />
                    </div>
                  </span>
                )}
                {!destinationList.length && sampleSettings ? (
                  <span className='use-sample'>
                    Please provide {destinationTypeName} connection settings or use Hevo's{' '}
                    <HdLink
                      dataId={dataIdGenerator('sample')}
                      type='button'
                      onClick={sampleSettingsClick}
                    >
                      Sample {destinationTypeName}
                    </HdLink>
                  </span>
                ) : null}
              </div>

              {destinationTypeMetaData.setupWarning ? (
                <HdPane className='mt-2 mb-8' variant='warning-faded' icon='error-filled'>
                  <span>{destinationTypeMetaData.setupWarning}</span>
                </HdPane>
              ) : null}

              <DestinationSettingsForm
                destinationType={destinationType}
                destinationMode={destinationMode}
                updateConnection={false}
                hevoEntityFor={hevoEntityType}
                draftDestination={draftDestination}
                selectedExistingDestination={selectedExistingDestination}
                frozenFields={frozenFields}
                onConnectionError={onConnectionError}
                onConnectionSuccess={onConnectionSuccess}
                onFormDirty={onFormDirty}
                onFormFieldChange={onFormFieldChange}
                onFormFieldFocusIn={onFormFieldFocusIn}
                onFormDataChanges={onFormDataChanges}
                destination={destination}
                onSubmit={onSubmit}
                onInviteMember={inviteMember}
              />
            </div>
          ) : null}

          <AddMemberDialog
            open={addMemberDialogVisible}
            dialogBodyData={addMemberDialogData}
            onClose={closeAddMemberDialog}
          />
        </div>
      </div>

      {canShowSideBySideDocs ? (
        <div className={styles.setupGuideDocs}>
          <SetupGuideDocs
            sourceTypeIdentifier={destinationType.dest_type}
            docLink={docLink}
            loadingParent={loading}
            areTabsRendered={DESTINATION_TYPES[destinationType.dest_type].showSBSTabs}
          />
        </div>
      ) : null}

      <AskSupportDialog
        open={askSupportDialogData.open}
        data={askSupportDialogData.data}
        onClose={_askSupportDialogService.onClose.bind(_askSupportDialogService)}
      />
    </div>
  );
}
